import api from '../../services/api';
import { OPERATOR_REQUEST_CONFIG, warehousePath } from '../operator/operatorApi';

/*
 * Packing. The floor calls an item a «bulto». Every write answers with the
 * whole order as packing sees it, plus `packed_now` when that write closed the
 * order and `replayed`. The order brings `gates`, `labels` and, once ready,
 * `next_order` and `queue_count`; a server that does not send them yet leaves
 * the gates to be worked out from the lines and items, and the rest unshown.
 * The packing writes carry no idempotency key: an addition says what
 * the screen showed before it (`items_count`, `in_item`), and the server tells
 * a retry whose first answer was lost (200 with `replayed: true`) from a new
 * action (201) by that count, or answers 412 when the screen is behind it. It
 * only holds when the screen sends one action at a time and repeats a lost one
 * before any other. Sending the order to its courier, or creating its shipment
 * there again, is the exception: it carries a key, because it leaves the
 * warehouse.
 */

export const getPackingOrdersRequest = (warehouseId, { page = 1 } = {}) =>
  api.get(warehousePath(warehouseId, 'packing/orders/'), { ...OPERATOR_REQUEST_CONFIG, params: { page } });

export const getPackingOrderRequest = (warehouseId, orderId) =>
  api.get(warehousePath(warehouseId, `packing/orders/${orderId}/`), OPERATOR_REQUEST_CONFIG);

/** `{items_count?}`: how many items the screen shows. A replay answers the `item_id` it had made. */
export const addItemRequest = (warehouseId, orderId, body = {}) =>
  api.post(warehousePath(warehouseId, `packing/orders/${orderId}/items/`), body, OPERATOR_REQUEST_CONFIG);

/**
 * `{weight_grams?, height_cm?, width_cm?, length_cm?, products?: [{line_id, quantity}]}`: a measure
 * left out stays, `null` clears it; products set, not add.
 */
export const updateItemRequest = (warehouseId, itemId, body) =>
  api.patch(warehousePath(warehouseId, `packing/items/${itemId}/update/`), body, OPERATOR_REQUEST_CONFIG);

/**
 * `{product_code, quantity, line_id?, in_item?}`: adds to what the item has. `in_item` is the
 * units of that product the screen shows in the item; without `line_id` the server fills the
 * product's lines in order, a box across them if it has to.
 */
export const addItemProductRequest = (warehouseId, itemId, body) =>
  api.post(warehousePath(warehouseId, `packing/items/${itemId}/products/`), body, OPERATOR_REQUEST_CONFIG);

/** Sent again after it had gone through, it answers 404: the item is no longer there. */
export const removeItemRequest = (warehouseId, itemId) =>
  api.delete(warehousePath(warehouseId, `packing/items/${itemId}/delete/`), OPERATOR_REQUEST_CONFIG);

/**
 * `{supply_code | supply_id, quantity, in_item?}`: a box also fills the item's empty measures.
 * `in_item` is how much of that supply the screen shows in the item.
 */
export const addItemSupplyRequest = (warehouseId, itemId, body) =>
  api.post(warehousePath(warehouseId, `packing/items/${itemId}/supplies/`), body, OPERATOR_REQUEST_CONFIG);

/** @param {number} supplyUseId - `items[].supplies[].id`; sent again after it had gone through, 404. */
export const removeItemSupplyRequest = (warehouseId, supplyUseId) =>
  api.delete(warehousePath(warehouseId, `packing/supplies/${supplyUseId}/delete/`), OPERATOR_REQUEST_CONFIG);

/**
 * `{idempotency_key}`: sends the packed order to its courier, which makes the
 * shipment and its labels in the background → the order as packing sees it,
 * its `courier_send` out (`pending`), and `replayed`. Sent again under the same
 * key, or while an attempt is out, it sends nothing more; a 400 says why the
 * order cannot go.
 */
export const sendToCourierRequest = (warehouseId, orderId, body) =>
  api.post(warehousePath(warehouseId, `packing/orders/${orderId}/courier/`), body, OPERATOR_REQUEST_CONFIG);

/**
 * `{idempotency_key}`: creates the order's shipment again at its courier with
 * the items it has now, once the floor changed them after it was labeled —
 * the server does it by itself when packing is done; this is the packer's,
 * after that one failed or did not start → the order as packing sees it, its
 * `relabel` out (`pending`), and `replayed`. Sent again under the same key, or
 * while one is out, it makes nothing more; a 400 says why it cannot be made.
 */
export const relabelRequest = (warehouseId, orderId, body) =>
  api.post(warehousePath(warehouseId, `packing/orders/${orderId}/relabel/`), body, OPERATOR_REQUEST_CONFIG);
